/**
 * PipelineReconcileDashboard.tsx
 *
 * Main UI for /sources/reconcile (Pipeline Reconcile), SYSTEM_ADMIN only.
 *
 * Sections:
 *   1. Summary health cards (aggregate counts + last refreshed + auto-refresh toggle)
 *   2. Toolbar: Refresh, Preview, Run
 *   3. Error / action-result banners (dismissible)
 *   4. Live progress panel (collapsible, auto-collapses after queue empties)
 *   5. Stuck-artifacts table (per-jurisdiction, sortable, simplified columns)
 *   6. Recent reconciliation runs table (relative time, color-coded)
 */
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/apiFetch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  usePipelineProgress,
  type PipelineProgressData,
  type StageProgress,
} from '@/hooks/usePipelineProgress';
import {
  usePipelineReconcile,
  type JurisdictionReconcileRow,
} from '@/hooks/usePipelineReconcile';
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  PlayCircle,
  Eye,
  ExternalLink,
  Clock,
  Activity,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  X,
  Layers,
  Users,
  FileText,
  RotateCcw,
  Trash2,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtCategoryLabel(key: string): string {
  const map: Record<string, string> = {
    no_extracted_text: 'No extracted text',
    extracted_no_chunks: 'Extracted, no chunks',
    chunks_not_embedded: 'Chunks not embedded',
    minutes_no_attendees: 'Minutes, no attendees',
    minutes_no_motions: 'Minutes, no motions',
    minutes_no_financial: 'Minutes, no financials',
  };
  return map[key] ?? key;
}

function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleString();
}

function fmtRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 10) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return fmtWhen(iso);
}

function CountBadge({
  n,
  tone,
}: {
  n: number | null | undefined;
  tone: 'neutral' | 'warn' | 'danger' | 'success';
}) {
  const safeN = n ?? 0;
  if (safeN === 0 && tone !== 'danger')
    return <span className="text-gray-400">0</span>;
  const classes =
    tone === 'danger'
      ? 'bg-red-100 text-red-700 border-red-200'
      : tone === 'warn'
        ? 'bg-amber-100 text-amber-700 border-amber-200'
        : tone === 'success'
          ? 'bg-green-100 text-green-700 border-green-200'
          : 'bg-gray-100 text-gray-600 border-gray-200';
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded text-xs font-bold border ${classes}`}
    >
      {safeN.toLocaleString()}
    </span>
  );
}

function RetryBreakdown({
  breakdown,
  title,
}: {
  breakdown: Record<string, number> | undefined;
  title?: string;
}) {
  if (!breakdown) return null;
  const entries = ['0', '1', '2', '3+'].map(
    (k) => [k, breakdown[k] ?? 0] as const
  );
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (total === 0) return null;
  return (
    <div
      className="text-[10px] text-gray-500 mt-0.5 whitespace-nowrap"
      title={title}
    >
      {entries
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${k}\u00d7:${v}`)
        .join(' ')}
    </div>
  );
}

/** Badge for the Jobs cell — shows pending, running, timed-out, failed as a compact string. */
function JobsBadge({ row }: { row: JurisdictionReconcileRow }) {
  const parts: React.ReactNode[] = [];
  if (row.jobs_pending > 0)
    parts.push(
      <span key="p" className="text-gray-600">
        {row.jobs_pending} pending
      </span>
    );
  if (row.jobs_running > 0)
    parts.push(
      <span key="r" className="text-blue-600">
        {row.jobs_running} running
      </span>
    );
  if (row.jobs_running_past_timeout > 0)
    parts.push(
      <span key="t" className="text-red-600 font-medium">
        {row.jobs_running_past_timeout} timed out
      </span>
    );
  if (row.jobs_failed > 0)
    parts.push(
      <span key="f" className="text-red-700 font-medium">
        {row.jobs_failed} failed
      </span>
    );
  if (parts.length === 0) return <span className="text-gray-300">&mdash;</span>;
  return (
    <div className="flex flex-wrap gap-x-1.5 gap-y-0.5 text-[11px]">
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-gray-300">|</span>}
          {p}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Live progress panel ──────────────────────────────────────────────────────

function fmtTickerWhen(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - d.getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  return d.toLocaleTimeString();
}

const STAGE_LABEL: Record<string, string> = {
  extract: 'Extract',
  embed: 'Embed',
  attendees: 'Attendees',
  motions: 'Motions',
};

function LiveProgressPanel({
  data,
  polling,
  everActive,
  error,
}: {
  data: PipelineProgressData | null;
  polling: boolean;
  everActive: boolean;
  error: string | null;
}) {
  const [open, setOpen] = useState(true);
  const autoCollapseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevActiveRef = useRef(false);
  const contentId = 'live-progress-content';

  useEffect(() => {
    const isActive = !!data?.active;
    if (isActive) {
      prevActiveRef.current = true;
      if (autoCollapseRef.current) {
        clearTimeout(autoCollapseRef.current);
        autoCollapseRef.current = null;
      }
    } else if (prevActiveRef.current) {
      autoCollapseRef.current = setTimeout(() => setOpen(false), 10_000);
      prevActiveRef.current = false;
    }
    return () => {
      if (autoCollapseRef.current) clearTimeout(autoCollapseRef.current);
    };
  }, [data?.active]);

  const handleToggle = useCallback(() => {
    setOpen((o) => !o);
  }, []);

  if (!data && !everActive) return null;

  const totals = data?.totals ?? {
    pending: 0,
    running: 0,
    done: 0,
    failed: 0,
    total: 0,
  };
  const complete = totals.done + totals.failed;
  const pct =
    totals.total > 0 ? Math.floor((complete / totals.total) * 1000) / 10 : 0;
  const isActive = data?.active ?? false;
  const showCompletionSummary = everActive && !isActive && data !== null;
  const loading = polling && !data;

  const statusColor = error
    ? 'red'
    : totals.failed > 0
      ? 'amber'
      : isActive
        ? 'blue'
        : totals.total > 0
          ? 'green'
          : 'gray';

  const statusDot: Record<string, string> = {
    red: 'bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.5)]',
    amber: 'bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.5)]',
    blue: 'bg-blue-500 shadow-[0_0_6px_rgba(59,130,246,0.5)]',
    green: 'bg-green-500',
    gray: 'bg-gray-300',
  };

  function stageBorder(s: StageProgress): string {
    if (s.failed > 0) return 'border-l-red-400';
    if (s.running > 0) return 'border-l-blue-400';
    if (s.done === s.total && s.total > 0) return 'border-l-green-400';
    return 'border-l-gray-200';
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleToggle}
            className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-gray-700 group"
            aria-expanded={open}
            aria-controls={contentId}
            aria-label={
              open ? 'Collapse live progress' : 'Expand live progress'
            }
          >
            <span
              className={`relative flex h-2.5 w-2.5 rounded-full ${statusDot[statusColor]} ${isActive ? 'animate-pulse' : ''}`}
            />
            <Activity className="w-4 h-4 text-gray-500 group-hover:text-gray-700" />
            Live progress
            {open ? (
              <ChevronUp className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600" />
            )}
          </button>

          <div className="flex items-center gap-2">
            {error && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-0.5">
                <AlertTriangle className="w-3 h-3" />
                Error
              </span>
            )}
            {!error && isActive && totals.running > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-0.5">
                <span className="relative flex h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
                Processing
              </span>
            )}
            {!error && isActive && totals.running === 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2.5 py-0.5">
                <span className="relative flex h-1.5 w-1.5 rounded-full bg-indigo-500 animate-pulse" />
                Queued
              </span>
            )}
            {!error && !isActive && totals.total > 0 && !loading && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
                <CheckCircle2 className="w-3 h-3" />
                Complete
              </span>
            )}
            {!error && loading && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2.5 py-0.5">
                <Loader2 className="w-3 h-3 animate-spin" />
                Waiting
              </span>
            )}
            {polling && data && (
              <span className="text-[10px] text-gray-400 hidden sm:inline">
                every 3s
              </span>
            )}
          </div>
        </div>

        {error && (
          <p className="text-xs text-red-600 mt-2" role="alert">
            Progress polling error: {error}
          </p>
        )}

        {showCompletionSummary && !open && (
          <div className="mt-2 flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
            <span>
              Queue empty &mdash; {totals.done.toLocaleString()} done
              {totals.failed > 0 &&
                `, ${totals.failed.toLocaleString()} failed`}
            </span>
          </div>
        )}

        {!open && !showCompletionSummary && (data || everActive) && (
          <div className="mt-2 text-xs text-gray-500 flex items-center gap-1.5">
            {isActive ? (
              <>
                {totals.running > 0 ? (
                  <span className="inline-flex items-center gap-1 font-medium text-blue-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
                    {totals.running.toLocaleString()} running
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 font-medium text-indigo-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 animate-pulse" />
                    {totals.pending.toLocaleString()} queued
                  </span>
                )}
                <span className="text-gray-300">|</span>
                <span>
                  {totals.pending.toLocaleString()} pending,{' '}
                  {complete.toLocaleString()} complete
                </span>
              </>
            ) : (
              <span>{complete.toLocaleString()} total jobs processed</span>
            )}
            <span className="text-gray-400 ml-1">&mdash; tap to expand</span>
          </div>
        )}

        <div
          id={contentId}
          className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? 'mt-3 opacity-100' : 'max-h-0 opacity-0 mt-0'}`}
        >
          {open && data && totals.total > 0 && (
            <>
              <div className="mb-4">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1.5">
                  <span className="font-medium text-gray-700">
                    {complete.toLocaleString()} of{' '}
                    {totals.total.toLocaleString()} complete
                  </span>
                  <span className="inline-flex items-center justify-center min-w-[2.5rem] h-5 rounded-full bg-gray-100 text-xs font-bold text-gray-700">
                    {pct}%
                  </span>
                </div>
                <div
                  className="h-3 rounded-full bg-gray-100 overflow-hidden relative"
                  role="progressbar"
                  aria-valuenow={complete}
                  aria-valuemin={0}
                  aria-valuemax={totals.total}
                  aria-label={`Pipeline progress: ${pct}% complete`}
                >
                  <div
                    className={`h-full rounded-full transition-all duration-700 ease-out relative overflow-hidden ${
                      totals.failed > 0
                        ? 'bg-gradient-to-r from-amber-400 to-amber-500'
                        : 'bg-gradient-to-r from-blue-500 to-blue-600'
                    }`}
                    style={{ width: `${pct}%` }}
                  >
                    <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.15)_25%,rgba(255,255,255,0.15)_50%,transparent_50%,transparent_75%,rgba(255,255,255,0.15)_75%)] bg-[length:1rem_1rem] animate-[move-stripe_1s_linear_infinite]" />
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-500">
                  <span>
                    Pending:{' '}
                    <strong className="text-gray-700">
                      {totals.pending.toLocaleString()}
                    </strong>
                  </span>
                  <span>
                    Running:{' '}
                    <strong className="text-blue-700">
                      {totals.running.toLocaleString()}
                    </strong>
                  </span>
                  <span>
                    Done:{' '}
                    <strong className="text-green-700">
                      {totals.done.toLocaleString()}
                    </strong>
                  </span>
                  {totals.failed > 0 && (
                    <span>
                      Failed:{' '}
                      <strong className="text-red-700">
                        {totals.failed.toLocaleString()}
                      </strong>
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
                {data.stages
                  .filter((s) => s.total > 0)
                  .map((s) => (
                    <div
                      key={s.jobType}
                      className={`rounded-md border border-gray-200 bg-white pl-3 pr-2.5 py-2 border-l-4 ${stageBorder(s)}`}
                    >
                      <div className="text-[11px] font-semibold text-gray-800 mb-1">
                        {s.label}
                      </div>
                      <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px]">
                        <span className="text-gray-500">
                          <span className="font-medium text-gray-700">
                            {s.pending}
                          </span>{' '}
                          pending
                        </span>
                        <span className="text-blue-600">
                          <span className="font-medium">{s.running}</span>{' '}
                          running
                        </span>
                        <span className="text-green-600">
                          <span className="font-medium">{s.done}</span> done
                        </span>
                        {s.failed > 0 && (
                          <span className="text-red-600">
                            <span className="font-medium">{s.failed}</span>{' '}
                            failed
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
              </div>

              {data.ticker.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      Recently completed
                    </p>
                    <span className="text-[10px] text-gray-400">
                      {data.ticker.length} job
                      {data.ticker.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div
                    className="max-h-48 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-100"
                    aria-live="polite"
                    aria-label="Recently completed jobs ticker"
                  >
                    {data.ticker.map((entry, i) => {
                      const isFailure = entry.outcome === 'failed';
                      const isReview = entry.outcome === 'needs_review';
                      const rowBg = isFailure
                        ? 'bg-red-50/60 hover:bg-red-50'
                        : isReview
                          ? 'bg-amber-50/60 hover:bg-amber-50'
                          : 'hover:bg-gray-50';
                      return (
                        <div
                          key={`${entry.artifactId}-${i}`}
                          className={`flex items-center gap-2 text-xs py-1.5 px-2.5 transition-colors ${rowBg}`}
                        >
                          {isFailure ? (
                            <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                          ) : isReview ? (
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          ) : (
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                          )}
                          <span className="font-mono text-gray-500 shrink-0 text-[10px]">
                            {entry.meetingDate ?? '(no date)'}
                          </span>
                          <span className="text-gray-700 shrink-0 font-medium">
                            {entry.meetingType ?? entry.stage}
                          </span>
                          <span className="text-gray-300 shrink-0">&rarr;</span>
                          <span className="text-gray-800 truncate min-w-0">
                            {entry.detail ??
                              `${STAGE_LABEL[entry.stage] ?? entry.stage} ${entry.outcome}`}
                          </span>
                          <span className="ml-auto text-gray-400 shrink-0 whitespace-nowrap">
                            {fmtTickerWhen(entry.createdAt)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {data.stages.filter((s) => s.total > 0).length === 0 &&
                data.ticker.length === 0 && (
                  <div className="flex items-center justify-center py-6 text-sm text-gray-400">
                    <Clock className="w-4 h-4 mr-1.5" />
                    Pipeline is idle &mdash; no jobs to display
                  </div>
                )}
            </>
          )}

          {open && loading && (
            <div className="flex items-center justify-center py-6 text-sm text-gray-400">
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              Waiting for pipeline activity&hellip;
            </div>
          )}

          {open && data && totals.total === 0 && !loading && (
            <div className="flex items-center justify-center py-6 text-sm text-gray-400">
              <Clock className="w-4 h-4 mr-1.5" />
              No active jobs &mdash; pipeline is idle
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Summary health cards ──────────────────────────────────────────────────────

interface SummaryCardData {
  stuckTotal: number;
  activeJobs: number;
  timedOut: number;
  failedJobs: number;
  needsReview: number;
  nullJurisdiction: number;
}

function SummaryCards({
  summary,
  lastRefreshed,
  stale,
  autoRefresh,
  onToggleAutoRefresh,
  loading,
  onOpenFailed,
  failedCount,
}: {
  summary: SummaryCardData;
  lastRefreshed: Date | null;
  stale: boolean;
  autoRefresh: boolean;
  onToggleAutoRefresh: () => void;
  loading: boolean;
  onOpenFailed: () => void;
  failedCount: number;
}) {
  const cardClass = 'p-4 rounded-lg border shadow-sm';
  const labelClass = 'text-xs font-medium uppercase tracking-wide';

  if (loading) {
    return (
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
        aria-busy="true"
      >
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`${cardClass} border-gray-200 bg-gray-50 animate-pulse`}
          >
            <div className="h-3 w-20 bg-gray-200 rounded mb-2" />
            <div className="h-6 w-12 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {/* Stuck artifacts */}
      <div
        className={`${cardClass} ${summary.stuckTotal > 0 ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}
        title="Artifacts where a pipeline stage produced zero output — no extracted text, text without chunks, chunks without embeddings, or minutes with no attendees/motions/financial data. These can be requeued from the table below."
      >
        <div
          className={`${labelClass} ${summary.stuckTotal > 0 ? 'text-red-700' : 'text-green-700'}`}
        >
          <Layers className="w-3.5 h-3.5 inline mr-1" />
          Stuck artifacts
        </div>
        <p
          className={`text-2xl font-bold mt-1 ${summary.stuckTotal > 0 ? 'text-red-800' : 'text-green-800'}`}
        >
          {summary.stuckTotal.toLocaleString()}
        </p>
        <p className="text-xs text-gray-600 mt-0.5">
          Across all pipeline stages
        </p>
      </div>

      {/* Active jobs */}
      <div
        className={`${cardClass} ${summary.activeJobs > 0 || summary.failedJobs > 0 ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}
        title="Worker jobs currently processing on the VPS (running) or queued (pending). Failed jobs are stuck and need to be reset — click the 'failed' link to reset them."
      >
        <div
          className={`${labelClass} ${summary.activeJobs > 0 ? 'text-blue-700' : 'text-gray-500'}`}
        >
          <Activity className="w-3.5 h-3.5 inline mr-1" />
          Active jobs
        </div>
        <p
          className={`text-2xl font-bold mt-1 ${summary.activeJobs > 0 ? 'text-blue-800' : 'text-gray-700'}`}
        >
          {summary.activeJobs.toLocaleString()}
        </p>
        <p className="text-xs text-gray-600 mt-0.5">
          {summary.timedOut > 0 && (
            <button
              onClick={onOpenFailed}
              className="text-red-600 font-medium hover:text-red-800 hover:underline cursor-pointer"
            >
              {summary.timedOut} timed out
            </button>
          )}
          {summary.timedOut > 0 &&
            (summary.failedJobs > 0 || failedCount > 0) &&
            ', '}
          {(summary.failedJobs > 0 || failedCount > 0) && (
            <button
              onClick={onOpenFailed}
              className="text-red-600 font-medium hover:text-red-800 hover:underline cursor-pointer"
            >
              {failedCount > 0 ? failedCount : summary.failedJobs} failed
            </button>
          )}
          {summary.timedOut === 0 &&
            summary.failedJobs === 0 &&
            failedCount === 0 && (
              <span className="text-gray-500">queue clear</span>
            )}
        </p>
      </div>

      {/* Needs review */}
      <Link
        to="/needs-review"
        className={`${cardClass} cursor-pointer hover:shadow-md transition-shadow ${
          summary.needsReview > 0
            ? 'border-amber-200 bg-amber-50'
            : 'border-gray-200 bg-gray-50'
        }`}
        title="Artifacts where extraction was attempted but failed after retries (attendees_needs_review, votes_needs_review, or financial_needs_review). These require manual review on the Artifacts Review page — they cannot be fixed by re-queuing alone."
      >
        <div
          className={`${labelClass} ${summary.needsReview > 0 ? 'text-amber-700' : 'text-gray-500'}`}
        >
          <Users className="w-3.5 h-3.5 inline mr-1" />
          Needs review
        </div>
        <p
          className={`text-2xl font-bold mt-1 ${summary.needsReview > 0 ? 'text-amber-800' : 'text-gray-700'}`}
        >
          {summary.needsReview.toLocaleString()}
        </p>
        <p className="text-xs text-gray-600 mt-0.5">
          {summary.nullJurisdiction > 0 && (
            <span>
              {summary.nullJurisdiction.toLocaleString()} null-jurisdiction
              artifacts
            </span>
          )}
        </p>
      </Link>

      {/* Last refreshed */}
      <div
        className={`${cardClass} ${stale ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'}`}
      >
        <div
          className={`${labelClass} ${stale ? 'text-amber-700' : 'text-gray-500'}`}
        >
          <Clock className="w-3.5 h-3.5 inline mr-1" />
          Last refreshed
        </div>
        <p
          className={`text-lg font-bold mt-1 ${stale ? 'text-amber-800' : 'text-gray-800'}`}
        >
          {lastRefreshed ? lastRefreshed.toLocaleTimeString() : '\u2014'}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          {stale && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-700">
              <AlertTriangle className="w-3 h-3" />
              stale
            </span>
          )}
          <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={onToggleAutoRefresh}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Auto-refresh
          </label>
        </div>
      </div>
    </div>
  );
}

// ── Sortable table header ──────────────────────────────────────────────────────

type SortDir = 'asc' | 'desc';

function SortableHeader({
  label,
  sortKey,
  currentSort,
  currentDir,
  onSort,
  sub,
  className,
}: {
  label: string;
  sortKey: string;
  currentSort: string;
  currentDir: SortDir;
  onSort: (key: string) => void;
  sub?: string;
  className?: string;
}) {
  const active = currentSort === sortKey;
  const dir = active ? currentDir : null;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide hover:text-gray-900"
        aria-sort={
          active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined
        }
      >
        {label}
        {dir ? (
          dir === 'asc' ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          )
        ) : (
          <span className="w-3 h-3" />
        )}
      </button>
      {sub && (
        <div className="text-[10px] font-normal text-gray-400 normal-case mt-0.5">
          {sub}
        </div>
      )}
    </TableHead>
  );
}

// ── Action result banner (dismissible) ─────────────────────────────────────────

function ActionResultBanner({
  actionResult,
  actionError,
  actionLoading,
  onDismiss,
}: {
  actionResult: ReturnType<typeof usePipelineReconcile>['actionResult'];
  actionError: string | null;
  actionLoading: 'preview' | 'run' | 'dedupe' | null;
  onDismiss: () => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (actionLoading) {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 flex items-center gap-2 text-sm text-blue-800">
        <Loader2 className="w-4 h-4 animate-spin" />
        {actionLoading === 'preview'
          ? 'Scanning pipeline for stuck artifacts\u2026'
          : actionLoading === 'dedupe'
            ? 'Removing duplicate jobs\u2026'
            : 'Requeuing stuck artifacts\u2026'}
      </div>
    );
  }

  if (actionError) {
    return (
      <div
        className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2 text-sm text-red-700"
        role="alert"
      >
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span className="flex-1">{actionError}</span>
        <button
          type="button"
          onClick={onDismiss}
          className="text-red-400 hover:text-red-600"
          aria-label="Dismiss error"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (!actionResult) return null;

  const runMode = actionResult.requeued ? 'run' : 'preview';
  const wouldRequeueEntries = actionResult.wouldRequeue
    ? Object.entries(actionResult.wouldRequeue).filter(([, v]) => v > 0)
    : [];
  const foundEntries = actionResult.found
    ? Object.entries(actionResult.found).filter(([, v]) => v > 0)
    : [];
  const requeuedEntries = actionResult.requeued
    ? Object.entries(actionResult.requeued).filter(([, v]) => v > 0)
    : [];
  const totalWouldRequeue = wouldRequeueEntries.reduce((s, [, v]) => s + v, 0);
  const totalFound = foundEntries.reduce((s, [, v]) => s + v, 0);
  const totalRequeued = requeuedEntries.reduce((s, [, v]) => s + v, 0);

  const hasCoverage =
    actionResult.attendeeCoverage ||
    actionResult.motionsCoverage ||
    actionResult.financialCoverage;

  const bannerClass =
    runMode === 'run'
      ? 'border-green-200 bg-green-50 text-green-800'
      : 'border-blue-200 bg-blue-50 text-blue-800';

  return (
    <div className={`rounded-lg border p-3 ${bannerClass}`} role="alert">
      <div className="flex items-start gap-2">
        {runMode === 'run' ? (
          <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
        ) : (
          <Eye className="w-5 h-5 shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">
            {runMode === 'run' ? 'Requeue complete' : 'Preview complete'}
          </p>
          <p className="text-xs mt-0.5 opacity-80">
            {runMode === 'run'
              ? `${totalRequeued.toLocaleString()} artifacts requeued`
              : `${totalWouldRequeue.toLocaleString()} artifacts would be requeued`}
            {totalFound > 0 && ` (${totalFound.toLocaleString()} found)`}
          </p>
          {hasCoverage && (
            <button
              type="button"
              onClick={() => setDetailsOpen((o) => !o)}
              className="inline-flex items-center gap-1 text-xs font-medium mt-1 opacity-80 hover:opacity-100"
              aria-expanded={detailsOpen}
            >
              {detailsOpen ? 'Hide details' : 'View details'}
              {detailsOpen ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 opacity-60 hover:opacity-100"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {detailsOpen && (
        <div className="mt-3 space-y-2 border-t border-current border-opacity-20 pt-3">
          {wouldRequeueEntries.length > 0 && (
            <div>
              <p className="text-xs font-semibold mb-1">Would requeue</p>
              <div className="flex flex-wrap gap-1.5">
                {wouldRequeueEntries.map(([k, v]) => (
                  <span
                    key={k}
                    className="inline-flex items-center gap-1 bg-white bg-opacity-60 border border-current border-opacity-20 rounded px-2 py-0.5 text-xs"
                  >
                    <span className="opacity-70">{fmtCategoryLabel(k)}:</span>
                    <span className="font-bold">{v.toLocaleString()}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {foundEntries.length > 0 && (
            <div>
              <p className="text-xs font-semibold mb-1">Found</p>
              <div className="flex flex-wrap gap-1.5">
                {foundEntries.map(([k, v]) => (
                  <span
                    key={k}
                    className="inline-flex items-center gap-1 bg-white bg-opacity-60 border border-current border-opacity-20 rounded px-2 py-0.5 text-xs"
                  >
                    <span className="opacity-70">{fmtCategoryLabel(k)}:</span>
                    <span className="font-bold">{v.toLocaleString()}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {requeuedEntries.length > 0 && (
            <div>
              <p className="text-xs font-semibold mb-1">Requeued</p>
              <div className="flex flex-wrap gap-1.5">
                {requeuedEntries.map(([k, v]) => (
                  <span
                    key={k}
                    className="inline-flex items-center gap-1 bg-white bg-opacity-60 border border-current border-opacity-20 rounded px-2 py-0.5 text-xs"
                  >
                    <span className="opacity-70">{fmtCategoryLabel(k)}:</span>
                    <span className="font-bold">{v.toLocaleString()}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {actionResult.attendeeCoverage && (
            <div>
              <p className="text-xs font-semibold mb-1">
                Attendee coverage (informational)
              </p>
              <div className="flex flex-wrap gap-1.5">
                <span className="inline-flex items-center gap-1 bg-white bg-opacity-60 border border-current border-opacity-20 rounded px-2 py-0.5 text-xs">
                  <span className="opacity-70">
                    Agendas, no attendees (expected):
                  </span>
                  <span className="font-bold">
                    {actionResult.attendeeCoverage.agendaNoAttendeesCount.toLocaleString()}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1 bg-white bg-opacity-60 border border-current border-opacity-20 rounded px-2 py-0.5 text-xs">
                  <span className="opacity-70">
                    Minutes, no attendees, needs review:
                  </span>
                  <span className="font-bold">
                    {actionResult.attendeeCoverage.minutesNoAttendeesNeedsReviewCount.toLocaleString()}
                  </span>
                </span>
                {['0', '1', '2', '3+'].map((k) => {
                  const v =
                    actionResult.attendeeCoverage!.minutesRetryBreakdown[k] ??
                    0;
                  if (v === 0) return null;
                  return (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1 bg-white bg-opacity-60 border border-current border-opacity-20 rounded px-2 py-0.5 text-xs"
                    >
                      <span className="opacity-70">retried {k}\u00d7:</span>
                      <span className="font-bold">{v.toLocaleString()}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {actionResult.motionsCoverage && (
            <div>
              <p className="text-xs font-semibold mb-1">
                Motions coverage (informational)
              </p>
              <div className="flex flex-wrap gap-1.5">
                <span className="inline-flex items-center gap-1 bg-white bg-opacity-60 border border-current border-opacity-20 rounded px-2 py-0.5 text-xs">
                  <span className="opacity-70">
                    Minutes, no motions, needs review:
                  </span>
                  <span className="font-bold">
                    {actionResult.motionsCoverage.minutesNoMotionsNeedsReviewCount.toLocaleString()}
                  </span>
                </span>
                {['0', '1', '2', '3+'].map((k) => {
                  const v =
                    actionResult.motionsCoverage!.minutesRetryBreakdown[k] ?? 0;
                  if (v === 0) return null;
                  return (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1 bg-white bg-opacity-60 border border-current border-opacity-20 rounded px-2 py-0.5 text-xs"
                    >
                      <span className="opacity-70">retried {k}\u00d7:</span>
                      <span className="font-bold">{v.toLocaleString()}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {actionResult.financialCoverage && (
            <div>
              <p className="text-xs font-semibold mb-1">
                Financials coverage (informational)
              </p>
              <div className="flex flex-wrap gap-1.5">
                <span className="inline-flex items-center gap-1 bg-white bg-opacity-60 border border-current border-opacity-20 rounded px-2 py-0.5 text-xs">
                  <span className="opacity-70">
                    Minutes, no financials, needs review:
                  </span>
                  <span className="font-bold">
                    {actionResult.financialCoverage.minutesNoFinancialNeedsReviewCount.toLocaleString()}
                  </span>
                </span>
                {['0', '1', '2', '3+'].map((k) => {
                  const v =
                    actionResult.financialCoverage!.minutesRetryBreakdown[k] ??
                    0;
                  if (v === 0) return null;
                  return (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1 bg-white bg-opacity-60 border border-current border-opacity-20 rounded px-2 py-0.5 text-xs"
                    >
                      <span className="opacity-70">retried {k}\u00d7:</span>
                      <span className="font-bold">{v.toLocaleString()}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main dashboard ─────────────────────────────────────────────────────────────

export const PipelineReconcileDashboard: React.FC = () => {
  const {
    data,
    runs,
    loading,
    error,
    runsError,
    lastRefreshed,
    refresh,
    refreshRuns,
    previewReconcile,
    runReconcile,
    actionLoading,
    actionResult,
    actionError,
    removeDedupes,
    dedupResult,
  } = usePipelineReconcile();

  const {
    data: progressData,
    polling: progressPolling,
    error: progressError,
    everActive: progressEverActive,
    startPolling: startProgressPolling,
  } = usePipelineProgress();

  // ── Auto-refresh ──────────────────────────────────────────────────────────
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [stale, setStale] = useState(false);

  // Failed jobs modal
  const [failedJobsOpen, setFailedJobsOpen] = useState(false);
  const [failedJobs, setFailedJobs] = useState<
    {
      id: string;
      type: string;
      artifact_id: string | null;
      source_id: string | null;
      reason: string;
      attempts: number;
    }[]
  >([]);
  const [failedJobsLoading, setFailedJobsLoading] = useState(false);
  const [failedCount, setFailedCount] = useState(0);

  const openFailedJobs = useCallback(async () => {
    setFailedJobsOpen(true);
    setFailedJobsLoading(true);
    try {
      const res = await apiFetch('/api/pipeline-reconcile/failed-jobs');
      if (res.ok) {
        const json = await res.json();
        const list = json.data ?? [];
        setFailedJobs(list);
        setFailedCount(list.length);
      }
    } catch {
      // ignore
    } finally {
      setFailedJobsLoading(false);
    }
  }, []);

  const handleResetFailed = useCallback(
    async (ids: string[]) => {
      setFailedJobsLoading(true);
      try {
        await apiFetch('/api/pipeline-reconcile/reset-failed', {
          method: 'POST',
          body: JSON.stringify({ job_ids: ids }),
        });
        setFailedJobs((prev) => prev.filter((j) => !ids.includes(j.id)));
        setFailedCount((c) => c - ids.length);
        void refresh();
      } catch {
        // ignore
      } finally {
        setFailedJobsLoading(false);
      }
    },
    [refresh]
  );

  const handleResetSingle = useCallback(
    (id: string) => {
      void handleResetFailed([id]);
    },
    [handleResetFailed]
  );

  const startInterval = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => void refresh(), 30_000);
  }, [refresh]);

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (autoRefresh) startInterval();
    else stopInterval();
    return stopInterval;
  }, [autoRefresh, startInterval, stopInterval]);

  // Pause interval when tab is hidden
  useEffect(() => {
    const handler = () => {
      if (document.hidden) stopInterval();
      else if (autoRefresh) startInterval();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [autoRefresh, startInterval, stopInterval]);

  // Stale indicator — checks every 15s whether data is >60s old.
  // Initial state is computed inline; the interval handles updates.
  useEffect(() => {
    const id = setInterval(() => {
      setStale(
        lastRefreshed ? Date.now() - lastRefreshed.getTime() > 60_000 : false
      );
    }, 15_000);
    return () => clearInterval(id);
  }, [lastRefreshed]);

  // Initial load
  useEffect(() => {
    void refresh();
    void refreshRuns();
    startProgressPolling();
  }, [refresh, refreshRuns, startProgressPolling]);

  // Auto-refresh table when progress queue empties
  const prevPollingRef = useRef(progressPolling);
  useEffect(() => {
    if (prevPollingRef.current && !progressPolling) {
      void refresh();
    }
    prevPollingRef.current = progressPolling;
  }, [progressPolling, refresh]);

  // ── Dismiss action result ────────────────────────────────────────────────
  // Track action version so banner re-appears when a new action starts/completes,
  // even if previously dismissed.
  const actionVersion =
    actionLoading === 'preview'
      ? 'preview-pending'
      : actionLoading === 'run'
        ? 'run-pending'
        : (actionResult?.runId ?? actionError ?? 'idle');

  const [dismissedAction, setDismissedAction] = useState(false);
  const prevVersionRef = useRef(actionVersion);

  if (actionVersion !== prevVersionRef.current) {
    prevVersionRef.current = actionVersion;
    if (actionVersion !== 'idle') setDismissedAction(false);
  }

  const handleDismissAction = useCallback(() => setDismissedAction(true), []);

  // ── Compute aggregate summary ────────────────────────────────────────────
  const jurisdictions = useMemo(
    () => (data?.jurisdictions ?? []) as JurisdictionReconcileRow[],
    [data?.jurisdictions]
  );

  const summary: SummaryCardData = useMemo(() => {
    const acc = jurisdictions.reduce(
      (a, r) => {
        a.stuckTotal +=
          r.no_extracted_text +
          r.extracted_no_chunks +
          r.chunks_not_embedded +
          r.minutes_no_attendees +
          r.minutes_no_motions +
          r.minutes_no_financial;
        a.activeJobs += r.jobs_pending + r.jobs_running;
        a.timedOut += r.jobs_running_past_timeout;
        a.failedJobs += r.jobs_failed;
        a.needsReview += r.needs_review_artifacts;
        return a;
      },
      {
        stuckTotal: 0,
        activeJobs: 0,
        timedOut: 0,
        failedJobs: 0,
        needsReview: 0,
        nullJurisdiction: 0,
      }
    );
    acc.nullJurisdiction = data?.nullJurisdictionArtifacts ?? 0;
    return acc;
  }, [jurisdictions, data?.nullJurisdictionArtifacts]);

  // ── Table sorting ────────────────────────────────────────────────────────
  const [sortCol, setSortCol] = useState('stuckTotal');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = useCallback((key: string) => {
    setSortCol((prev) => {
      if (prev === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      else setSortDir('desc');
      return key;
    });
  }, []);

  const sortedJurisdictions = useMemo(() => {
    const list = [...jurisdictions];
    list.sort((a, b) => {
      let av: number;
      let bv: number;
      switch (sortCol) {
        case 'noExtractedText':
          av = a.no_extracted_text;
          bv = b.no_extracted_text;
          break;
        case 'extractedNoChunks':
          av = a.extracted_no_chunks;
          bv = b.extracted_no_chunks;
          break;
        case 'chunksNotEmbedded':
          av = a.chunks_not_embedded;
          bv = b.chunks_not_embedded;
          break;
        case 'attendees':
          av = a.minutes_no_attendees;
          bv = b.minutes_no_attendees;
          break;
        case 'motions':
          av = a.minutes_no_motions;
          bv = b.minutes_no_motions;
          break;
        case 'financial':
          av = a.minutes_no_financial;
          bv = b.minutes_no_financial;
          break;
        case 'jobs':
          av =
            a.jobs_pending +
            a.jobs_running +
            a.jobs_running_past_timeout +
            a.jobs_failed;
          bv =
            b.jobs_pending +
            b.jobs_running +
            b.jobs_running_past_timeout +
            b.jobs_failed;
          break;
        case 'needsReview':
          av = a.needs_review_artifacts;
          bv = b.needs_review_artifacts;
          break;
        default: // stuckTotal
          av =
            a.no_extracted_text +
            a.extracted_no_chunks +
            a.chunks_not_embedded +
            a.minutes_no_attendees +
            a.minutes_no_motions +
            a.minutes_no_financial;
          bv =
            b.no_extracted_text +
            b.extracted_no_chunks +
            b.chunks_not_embedded +
            b.minutes_no_attendees +
            b.minutes_no_motions +
            b.minutes_no_financial;
      }
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return list;
  }, [jurisdictions, sortCol, sortDir]);

  const agendaTotal = useMemo(
    () => jurisdictions.reduce((s, r) => s + r.agenda_no_attendees, 0),
    [jurisdictions]
  );

  return (
    <>
      <div className="space-y-4">
        {/* ── Summary cards ── */}
        <SummaryCards
          summary={summary}
          lastRefreshed={lastRefreshed}
          stale={stale}
          autoRefresh={autoRefresh}
          onToggleAutoRefresh={() => setAutoRefresh((a) => !a)}
          loading={loading && !data}
          onOpenFailed={openFailedJobs}
          failedCount={failedCount}
        />

        {/* ── Toolbar ── */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            {stale && (
              <span className="inline-flex items-center gap-1 text-amber-700 font-medium bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                Data may be stale &mdash; Refresh
              </span>
            )}
            {summary.nullJurisdiction > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-700 font-medium bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                {summary.nullJurisdiction.toLocaleString()} artifacts with NULL
                jurisdiction
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refresh()}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-1" />
              )}
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={actionLoading !== null}
              onClick={() => void previewReconcile()}
            >
              {actionLoading === 'preview' ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Eye className="w-4 h-4 mr-1" />
              )}
              Preview reconcile
            </Button>
            <Button
              size="sm"
              disabled={actionLoading !== null}
              onClick={() => {
                void runReconcile();
                startProgressPolling();
              }}
            >
              {actionLoading === 'run' ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <PlayCircle className="w-4 h-4 mr-1" />
              )}
              Run reconcile
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={actionLoading !== null}
              onClick={() => void removeDedupes()}
            >
              {actionLoading === 'dedupe' ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-1" />
              )}
              Remove Dedupes
            </Button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            role="alert"
          >
            {error}
          </div>
        )}

        {/* ── Action result banner (dismissible) ── */}
        {!dismissedAction && (
          <ActionResultBanner
            actionResult={actionResult}
            actionError={actionError}
            actionLoading={actionLoading}
            onDismiss={handleDismissAction}
          />
        )}

        {/* ── Dedup result banner ── */}
        {dedupResult && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            <p className="font-medium mb-1">
              Removed {dedupResult.total.toLocaleString()} jobs
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1 text-xs">
              {Object.entries(dedupResult.redundant).map(([t, c]) => (
                <span key={'r-' + t}>
                  {t}: {c.toLocaleString()} redundant
                </span>
              ))}
              {Object.entries(dedupResult.duplicates).map(([t, c]) => (
                <span key={'d-' + t}>
                  {t}: {c.toLocaleString()} duplicates
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── Live progress panel (collapsible) ── */}
        <LiveProgressPanel
          data={progressData}
          polling={progressPolling}
          everActive={progressEverActive}
          error={progressError}
        />

        {/* ── Stuck artifacts table ── */}
        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-900">
                Stuck artifacts by jurisdiction
              </h2>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.05)]">
                      Jurisdiction
                    </TableHead>
                    <SortableHeader
                      label="Text Extr."
                      sortKey="noExtractedText"
                      currentSort={sortCol}
                      currentDir={sortDir}
                      onSort={handleSort}
                      sub=""
                    />
                    <SortableHeader
                      label="Chunking"
                      sortKey="extractedNoChunks"
                      currentSort={sortCol}
                      currentDir={sortDir}
                      onSort={handleSort}
                      sub=""
                    />
                    <SortableHeader
                      label="Embedding"
                      sortKey="chunksNotEmbedded"
                      currentSort={sortCol}
                      currentDir={sortDir}
                      onSort={handleSort}
                      sub=""
                    />
                    <SortableHeader
                      label="Attendees"
                      sortKey="attendees"
                      currentSort={sortCol}
                      currentDir={sortDir}
                      onSort={handleSort}
                      sub="stuck"
                    />
                    <SortableHeader
                      label="Motions"
                      sortKey="motions"
                      currentSort={sortCol}
                      currentDir={sortDir}
                      onSort={handleSort}
                      sub="stuck"
                    />
                    <SortableHeader
                      label="Financial"
                      sortKey="financial"
                      currentSort={sortCol}
                      currentDir={sortDir}
                      onSort={handleSort}
                      sub="stuck"
                    />
                    <SortableHeader
                      label="Jobs"
                      sortKey="jobs"
                      currentSort={sortCol}
                      currentDir={sortDir}
                      onSort={handleSort}
                      sub=""
                    />
                    <SortableHeader
                      label="Review"
                      sortKey="needsReview"
                      currentSort={sortCol}
                      currentDir={sortDir}
                      onSort={handleSort}
                      sub=""
                    />
                    <TableHead className="text-right">Artifacts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && jurisdictions.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        className="text-center text-gray-500 py-8"
                      >
                        <div className="inline-flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading\u2026
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && sortedJurisdictions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8">
                        <div className="inline-flex flex-col items-center gap-1 text-gray-500">
                          <CheckCircle2 className="w-8 h-8 text-green-400" />
                          <p className="text-sm font-medium text-gray-700">
                            All pipeline stages healthy
                          </p>
                          <p className="text-xs">No stuck artifacts found.</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {sortedJurisdictions.map((row) => (
                    <TableRow key={row.jurisdiction_id}>
                      <TableCell className="font-medium text-gray-900 sticky left-0 bg-white shadow-[2px_0_4px_-2px_rgba(0,0,0,0.05)]">
                        {row.jurisdiction_name}
                        {row.jurisdiction_path && (
                          <div className="text-xs text-gray-400 font-mono">
                            {row.jurisdiction_path}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <CountBadge
                          n={row.no_extracted_text}
                          tone={row.no_extracted_text > 0 ? 'warn' : 'neutral'}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <CountBadge
                          n={row.extracted_no_chunks}
                          tone={
                            row.extracted_no_chunks > 0 ? 'warn' : 'neutral'
                          }
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <CountBadge
                          n={row.chunks_not_embedded}
                          tone={
                            row.chunks_not_embedded > 0 ? 'warn' : 'neutral'
                          }
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <CountBadge
                          n={row.minutes_no_attendees}
                          tone={
                            row.minutes_no_attendees > 0 ? 'warn' : 'neutral'
                          }
                        />
                        <RetryBreakdown
                          breakdown={row.minutes_retry_breakdown}
                        />
                        {row.minutes_no_attendees_needs_review > 0 && (
                          <div className="text-[10px] text-amber-700 mt-0.5 whitespace-nowrap">
                            +{row.minutes_no_attendees_needs_review} review
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <CountBadge
                          n={row.minutes_no_motions}
                          tone={row.minutes_no_motions > 0 ? 'warn' : 'neutral'}
                        />
                        <RetryBreakdown
                          breakdown={row.motions_retry_breakdown}
                          title="Number of prior extract_motions attempts logged for still-eligible Minutes with 0 motions"
                        />
                        {row.minutes_no_motions_needs_review > 0 && (
                          <div className="text-[10px] text-amber-700 mt-0.5 whitespace-nowrap">
                            +{row.minutes_no_motions_needs_review} review
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <CountBadge
                          n={row.minutes_no_financial}
                          tone={
                            row.minutes_no_financial > 0 ? 'warn' : 'neutral'
                          }
                        />
                        <RetryBreakdown
                          breakdown={row.financial_retry_breakdown}
                          title="Number of prior extract_financial attempts logged for still-eligible Minutes with 0 financials"
                        />
                        {row.minutes_no_financial_needs_review > 0 && (
                          <div className="text-[10px] text-amber-700 mt-0.5 whitespace-nowrap">
                            +{row.minutes_no_financial_needs_review} review
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <JobsBadge row={row} />
                      </TableCell>
                      <TableCell className="text-center">
                        <CountBadge
                          n={
                            row.needs_review_artifacts +
                            row.minutes_no_attendees_needs_review +
                            row.minutes_no_motions_needs_review +
                            row.minutes_no_financial_needs_review
                          }
                          tone="warn"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Link
                          to={`/artifacts?jurisdiction=${row.jurisdiction_id}`}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {agendaTotal > 0 && (
              <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-500">
                <FileText className="w-3 h-3 inline mr-1" />
                Agendas with 0 attendees:{' '}
                <span className="font-medium">
                  {agendaTotal.toLocaleString()}
                </span>{' '}
                (expected &mdash; blank pre-meeting roll-call, not stuck)
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Recent reconciliation runs ── */}
        <Card>
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">
              Recent reconciliation runs
            </h2>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Found</TableHead>
                  <TableHead>Requeued</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runsError && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-red-600 text-sm py-4"
                    >
                      {runsError}
                    </TableCell>
                  </TableRow>
                )}
                {!runsError && runs.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-gray-500 py-6"
                    >
                      No reconciliation runs yet. Run a preview or reconcile to
                      see history here.
                    </TableCell>
                  </TableRow>
                )}
                {!runsError &&
                  runs.map((run) => {
                    const foundTotal = Object.values(run.findings ?? {}).reduce(
                      (a, b) => a + b,
                      0
                    );
                    const requeuedTotal = Object.values(
                      run.requeued ?? {}
                    ).reduce((a, b) => a + b, 0);
                    return (
                      <TableRow key={run.id}>
                        <TableCell
                          className="text-sm text-gray-700"
                          title={fmtWhen(run.created_at)}
                        >
                          {fmtRelativeTime(run.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={run.mode === 'run' ? 'default' : 'outline'}
                            className={
                              run.mode === 'run'
                                ? 'bg-blue-100 text-blue-700 border-blue-200'
                                : ''
                            }
                          >
                            {run.mode === 'run' ? 'Run' : 'Preview'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {foundTotal > 0
                            ? foundTotal.toLocaleString()
                            : '\u2014'}
                        </TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {run.mode === 'run' && requeuedTotal > 0
                            ? requeuedTotal.toLocaleString()
                            : '\u2014'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      {/* ── Failed Jobs Modal ────────────────────────────────────────── */}
      {failedJobsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setFailedJobsOpen(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Failed Jobs ({failedJobs.length})
              </h2>
              <div className="flex items-center gap-2">
                {failedJobs.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      handleResetFailed(failedJobs.map((j) => j.id))
                    }
                    disabled={failedJobsLoading}
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                    Reset All
                  </Button>
                )}
                <button
                  onClick={() => setFailedJobsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {failedJobsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                </div>
              ) : failedJobs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No failed jobs — queue is clear.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs font-bold">Type</TableHead>
                      <TableHead className="text-xs font-bold">
                        Target
                      </TableHead>
                      <TableHead className="text-xs font-bold">
                        Reason
                      </TableHead>
                      <TableHead className="text-xs font-bold w-16">
                        Tries
                      </TableHead>
                      <TableHead className="text-xs font-bold w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {failedJobs.map((j) => (
                      <TableRow key={j.id} className="border-b border-gray-100">
                        <TableCell className="text-xs font-mono text-gray-700">
                          {j.type}
                        </TableCell>
                        <TableCell className="text-xs text-gray-600">
                          {j.artifact_id
                            ? j.artifact_id.slice(0, 8) + '...'
                            : j.source_id
                              ? j.source_id.slice(0, 8) + '...'
                              : '\u2014'}
                        </TableCell>
                        <TableCell className="text-xs text-red-600 max-w-[200px] truncate">
                          {j.reason}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {j.attempts}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResetSingle(j.id)}
                            disabled={failedJobsLoading}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <RotateCcw className="w-3 h-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
